import type {
  CampaignCreationCapabilities,
  SmartCreationBrowserDraft,
  SmartCreationStrategyLane,
  SmartCreationStrategyOperation,
  SmartCreationTargetingStrategyBranch,
  SmartCreationTargetingStrategyKind,
} from "@adpilot/shared";
import {
  effectiveSmartCreationStrategyLanes,
  isSmartCreationStrategyTargetActive,
  smartCreationStrategyTargetLane,
} from "@adpilot/shared";
import { IconInfoCircle, IconRefresh } from "@tabler/icons-react";
import { cva } from "class-variance-authority";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { StrategyCategoryBrandEditor } from "@/components/smart-creation/StrategyCategoryBrandEditor";
import { StrategyContextSummary } from "@/components/smart-creation/StrategyContextSummary";
import { StrategyEvidenceDetails } from "@/components/smart-creation/StrategyEvidenceDetails";
import { StrategyTargetEditor } from "@/components/smart-creation/StrategyTargetEditor";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldError } from "@/components/ui/field";
import { Popover, PopoverPopup, PopoverTitle, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { useDelayedPending } from "@/hooks/use-delayed-pending";
import { cn } from "@/lib/utils";

type EditableStrategyKind = Exclude<SmartCreationTargetingStrategyKind, "automatic">;
type StrategyView =
  | { kind: "overview" }
  | { kind: "targets"; strategyKind: SmartCreationTargetingStrategyKind }
  | { kind: "category_brand"; strategyKind: "branded" | "competitor" };
type ReturnFocusTarget = {
  action: "targets" | "category-brand";
  strategyKind: SmartCreationTargetingStrategyKind;
};

interface StrategyStepProps {
  targetEditing?: boolean;
  plan: SmartCreationBrowserDraft;
  capabilities: CampaignCreationCapabilities;
  recommendationPending: boolean;
  mutationPending?: boolean;
  onApplyOperations: (
    operations: SmartCreationStrategyOperation[],
    expectedRevision: number,
  ) => Promise<boolean>;
  onAcceptRecommendation: (
    kind: SmartCreationTargetingStrategyKind,
    expectedRevision: number,
  ) => Promise<boolean>;
  onRefreshRecommendation: () => void;
  onLocalDirtyChange: (dirty: boolean) => void;
}

const STRATEGIES: SmartCreationTargetingStrategyKind[] = [
  "automatic",
  "competitor",
  "research",
  "performance",
  "branded",
];

interface MatrixChoice {
  lane: SmartCreationStrategyLane;
  label: string;
  mode?: "exact" | "expanded";
  matches?: readonly ("broad" | "phrase" | "exact")[];
}
const targetingBadgeVariants = cva("h-4 px-1 text-[9px] font-medium leading-3", {
  variants: {
    match: {
      broad:
        "border-badge-information-border bg-badge-information-background text-badge-information-foreground",
      expanded:
        "border-badge-information-border bg-badge-information-background text-badge-information-foreground",
      phrase: "border-match-phrase-border bg-match-phrase-background text-match-phrase-foreground",
      exact: "border-match-exact-border bg-match-exact-background text-match-exact-foreground",
    },
  },
});

const MATRIX_ROWS: Record<
  SmartCreationTargetingStrategyKind,
  { keywords: MatrixChoice[]; products: MatrixChoice[] }
> = {
  automatic: { keywords: [], products: [{ lane: "automatic", label: "automaticCampaign" }] },
  competitor: {
    keywords: [
      {
        lane: "competitor_keywords",
        label: "coreCompetitorKeywords",
        matches: ["broad", "phrase", "exact"],
      },
    ],
    products: [
      { lane: "competitor_product_exact", label: "singleProduct", mode: "exact" },
      { lane: "competitor_brand", label: "coreCompetitorBrand" },
    ],
  },
  research: {
    keywords: [
      { lane: "research_broad", label: "broadMatch", matches: ["broad"] },
      { lane: "research_phrase", label: "phraseMatch", matches: ["phrase"] },
    ],
    products: [
      { lane: "research_category", label: "productCategory" },
      { lane: "research_product_expanded", label: "singleProduct", mode: "expanded" },
    ],
  },
  performance: {
    keywords: [{ lane: "performance_exact", label: "exactMatch", matches: ["exact"] }],
    products: [{ lane: "performance_product_exact", label: "singleProduct", mode: "exact" }],
  },
  branded: {
    keywords: [
      { lane: "branded_keywords", label: "brandedKeywords", matches: ["broad", "phrase", "exact"] },
    ],
    products: [
      { lane: "branded_own_asins", label: "ownAsins", mode: "exact" },
      { lane: "branded_own_brand", label: "ownBrand" },
    ],
  },
};

function currentRecommendation(plan: SmartCreationBrowserDraft) {
  const recommendation = plan.strategyRecommendation;
  if (recommendation?.applicability !== "current") return null;
  if (Date.parse(recommendation.expiresAt) <= Date.now()) return null;
  return recommendation;
}

function recommendationRefreshReason(
  plan: SmartCreationBrowserDraft,
): "expired" | "superseded" | null {
  const recommendation = plan.strategyRecommendation;
  if (!recommendation) return null;
  if (
    recommendation.applicability === "expired" ||
    Date.parse(recommendation.expiresAt) <= Date.now()
  ) {
    return "expired";
  }
  return recommendation.applicability === "superseded" ? "superseded" : null;
}

function branchFor(plan: SmartCreationBrowserDraft, kind: SmartCreationTargetingStrategyKind) {
  const branch = plan.draft.strategy.branches.find((candidate) => candidate.kind === kind);
  if (!branch) throw new Error(`Missing ${kind} Smart Creation Strategy branch`);
  return branch;
}

/** A bundled B/P/E keyword is one selected lane, not three required source targets. */
export function getEmptySmartCreationStrategyLanes(
  branches: readonly SmartCreationTargetingStrategyBranch[],
): SmartCreationStrategyLane[] {
  return branches.flatMap((branch) =>
    branch.kind === "automatic"
      ? []
      : effectiveSmartCreationStrategyLanes(branch).filter(
          (lane) =>
            !branch.targets.some(
              (target) =>
                isSmartCreationStrategyTargetActive(branch, target) &&
                smartCreationStrategyTargetLane(branch.kind, target.intent) === lane,
            ),
        ),
  );
}

export function StrategyStep({
  targetEditing = false,
  plan,
  capabilities,
  recommendationPending,
  mutationPending = false,
  onApplyOperations,
  onAcceptRecommendation,
  onRefreshRecommendation,
  onLocalDirtyChange,
}: StrategyStepProps) {
  const { t } = useTranslation("smartCreation");
  const [view, setView] = useState<StrategyView>({ kind: "overview" });
  const [subviewDirty, setSubviewDirty] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const returnFocusRef = useRef<ReturnFocusTarget | null>(null);
  const recommendationFeedbackPending = useDelayedPending(recommendationPending);
  const mutationFeedbackPending = useDelayedPending(mutationPending);
  const recommendation = currentRecommendation(plan);
  const refreshReason = recommendationRefreshReason(plan);

  const returnToOverview = useCallback((): void => {
    setSubviewDirty(false);
    onLocalDirtyChange(false);
    setDiscardDialogOpen(false);
    setView({ kind: "overview" });
    window.setTimeout(() => {
      const target = returnFocusRef.current;
      if (!target) return;
      document
        .querySelector<HTMLElement>(
          `[data-strategy-kind="${target.strategyKind}"][data-strategy-action="${target.action}"]`,
        )
        ?.focus();
    }, 0);
  }, [onLocalDirtyChange]);

  const requestReturnToOverview = useCallback((): void => {
    if (mutationPending) return;
    if (subviewDirty) {
      setDiscardDialogOpen(true);
      return;
    }
    returnToOverview();
  }, [mutationPending, returnToOverview, subviewDirty]);

  const handleSubviewDirtyChange = useCallback(
    (dirty: boolean): void => {
      setSubviewDirty(dirty);
      onLocalDirtyChange(dirty);
    },
    [onLocalDirtyChange],
  );

  useEffect(
    () => () => {
      onLocalDirtyChange(false);
    },
    [onLocalDirtyChange],
  );

  useEffect(() => {
    if (view.kind === "overview") return;
    const headingId =
      view.kind === "targets"
        ? "smart-creation-target-editor-title"
        : "smart-creation-brand-editor-title";
    window.setTimeout(() => document.getElementById(headingId)?.focus(), 0);
  }, [view]);

  const handleSubviewKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLFieldSetElement>): void => {
      if (event.key !== "Escape" || event.defaultPrevented || discardDialogOpen) return;
      if (
        document.querySelector(
          '[data-slot="select-content"][data-open], [data-slot="popover-popup"][data-open], [data-slot="alert-dialog-popup"][data-open]',
        )
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      requestReturnToOverview();
    },
    [discardDialogOpen, requestReturnToOverview],
  );

  if (view.kind === "targets") {
    const branch = branchFor(plan, view.strategyKind);
    return (
      <>
        <fieldset
          aria-label={t("strategy.editor.title", {
            strategy: t(`strategy.${view.strategyKind}`),
          })}
          className="contents border-0 p-0"
          onKeyDown={handleSubviewKeyDown}
        >
          <StrategyTargetEditor
            key={`targets:${view.strategyKind}`}
            shopId={plan.shopId}
            planRevision={plan.revision}
            branch={branch}
            allBranches={plan.draft.strategy.branches}
            capabilities={capabilities}
            decision={plan.strategyDecisions[view.strategyKind]}
            diagnostics={plan.strategyDiagnostics}
            recommendation={recommendation}
            strategyCapabilities={plan.strategyCapabilities}
            mutationPending={mutationPending}
            onApply={onApplyOperations}
            onAcceptRecommendation={onAcceptRecommendation}
            onCancel={requestReturnToOverview}
            onComplete={returnToOverview}
            onDirtyChange={handleSubviewDirtyChange}
          />
        </fieldset>
        <StrategyDiscardDialog
          open={discardDialogOpen}
          onOpenChange={setDiscardDialogOpen}
          onDiscard={returnToOverview}
        />
      </>
    );
  }

  if (view.kind === "category_brand") {
    const branch = branchFor(plan, view.strategyKind);
    return (
      <>
        <fieldset
          aria-label={t("strategy.brandEditor.title")}
          className="contents border-0 p-0"
          onKeyDown={handleSubviewKeyDown}
        >
          <StrategyCategoryBrandEditor
            key={`brand:${view.strategyKind}`}
            planId={plan.id}
            shopId={plan.shopId}
            planRevision={plan.revision}
            branch={branch}
            allBranches={plan.draft.strategy.branches}
            capabilities={capabilities}
            mutationPending={mutationPending}
            onApply={onApplyOperations}
            onCancel={requestReturnToOverview}
            onComplete={returnToOverview}
            onDirtyChange={handleSubviewDirtyChange}
          />
        </fieldset>
        <StrategyDiscardDialog
          open={discardDialogOpen}
          onOpenChange={setDiscardDialogOpen}
          onDiscard={returnToOverview}
        />
      </>
    );
  }

  const branches = STRATEGIES.map((kind) => branchFor(plan, kind));
  const emptyLanes = getEmptySmartCreationStrategyLanes(branches);
  const manualTargets = branches
    .filter((branch) => branch.kind !== "automatic")
    .flatMap((branch) =>
      branch.targets.filter((target) => isSmartCreationStrategyTargetActive(branch, target)),
    ).length;
  const productPartitions =
    plan.draft.productOrganization === "campaign"
      ? new Set(plan.draft.products.map((product) => product.variationGroupKey)).size
      : 1;
  const budgetCannotFundManual =
    plan.draft.totalDailyBudget !== null &&
    plan.draft.totalDailyBudget <
      2 * Math.max(1, productPartitions) * capabilities.dailyBudget.min - 1e-8;
  const showBudgetLimitedPreset =
    recommendation?.evidence.status === "sufficient" &&
    recommendation.branches.some(
      (branch) => branch.kind !== "automatic" && branch.targets.some((target) => target.included),
    ) &&
    manualTargets === 0 &&
    budgetCannotFundManual &&
    STRATEGIES.filter((kind) => kind !== "automatic").every(
      (kind) => plan.strategyDecisions[kind].mode === "entered",
    );

  async function setLane(
    kind: EditableStrategyKind,
    lane: Exclude<SmartCreationStrategyLane, "automatic">,
    enabled: boolean,
  ): Promise<void> {
    await onApplyOperations(
      [{ operation: "set_lane_enabled", strategyKind: kind, lane, enabled }],
      plan.revision,
    );
  }

  function openTargets(
    _event: React.MouseEvent<HTMLElement>,
    strategyKind: SmartCreationTargetingStrategyKind,
  ): void {
    returnFocusRef.current = { action: "targets", strategyKind };
    setView({ kind: "targets", strategyKind });
  }

  function openBrandEditor(strategyKind: "branded" | "competitor"): void {
    returnFocusRef.current = { action: "category-brand", strategyKind };
    setView({ kind: "category_brand", strategyKind });
  }

  function renderChoice(kind: SmartCreationTargetingStrategyKind, choice: MatrixChoice) {
    const branch = branchFor(plan, kind);
    const laneCapability = plan.strategyCapabilities.lanes?.[choice.lane];
    const brandLane = choice.lane === "competitor_brand" || choice.lane === "branded_own_brand";
    const hasBrandTarget =
      brandLane &&
      branch.targets.some((target) => target.included && target.intent.type === "category_brand");
    const categoryUnsupported =
      choice.lane === "research_category" &&
      !plan.strategyCapabilities.targetIntents.category.supported;
    const unsupported =
      categoryUnsupported ||
      (laneCapability?.supported === false && laneCapability.reason !== "verification_required");
    const unavailableReason =
      brandLane && !hasBrandTarget
        ? t("strategy.matrix.configureBrandFirst")
        : categoryUnsupported
          ? t("strategy.matrix.unsupportedCategoryReason")
          : laneCapability?.supported === false
            ? t(`strategy.matrix.capabilityReason.${laneCapability.reason}`)
            : null;
    const checked = effectiveSmartCreationStrategyLanes(branch).includes(choice.lane);
    const needsTargets =
      targetEditing && checked && !unsupported && emptyLanes.includes(choice.lane);
    const issueId = `smart-creation-lane-${choice.lane}-issue`;
    const label = t(`strategy.matrix.${choice.label}`);
    return (
      <div
        key={choice.lane}
        data-strategy-lane={choice.lane}
        className="flex min-h-12 items-center gap-2 px-3 py-2"
      >
        <Checkbox
          id={`smart-creation-lane-${choice.lane}`}
          checked={checked}
          aria-invalid={needsTargets}
          aria-describedby={needsTargets ? issueId : undefined}
          disabled={kind === "automatic" || unsupported || mutationPending}
          aria-label={`${t(`strategy.${kind}`)} · ${label}`}
          onCheckedChange={(enabled) => {
            if (kind !== "automatic" && choice.lane !== "automatic")
              void setLane(kind, choice.lane, enabled);
          }}
        />
        <label
          htmlFor={`smart-creation-lane-${choice.lane}`}
          className="flex min-w-0 flex-1 cursor-pointer flex-col gap-0.5 text-left text-[13px]"
        >
          <span className="flex flex-wrap items-center gap-1.5">
            {choice.mode ? (
              <Badge
                variant="outline"
                size="compact"
                data-targeting-match={choice.mode}
                className={targetingBadgeVariants({ match: choice.mode })}
              >
                {t(`strategy.matrix.${choice.mode}`)}
              </Badge>
            ) : null}
            {label}
            {choice.matches ? (
              <span className="inline-flex items-center gap-1">
                {choice.matches.map((match) => (
                  <Badge
                    key={match}
                    variant="outline"
                    size="compact"
                    data-targeting-match={match}
                    title={t(`review.match.${match}`)}
                    className={targetingBadgeVariants({ match })}
                  >
                    {match.slice(0, 1).toUpperCase()}
                  </Badge>
                ))}
              </span>
            ) : null}
          </span>
          {unsupported ? (
            <span
              className="text-[10px] leading-4 text-muted-foreground"
              title={brandLane ? t("strategy.matrix.configureBrandDetail") : undefined}
            >
              {unavailableReason}
            </span>
          ) : null}
          {needsTargets ? (
            <span
              id={issueId}
              data-empty-strategy-lane={choice.lane}
              className="text-[11px] leading-4 text-destructive"
            >
              {t("strategy.matrix.emptyLane", {
                defaultValue: "Add targets or turn off this option.",
              })}
            </span>
          ) : null}
        </label>
        {targetEditing && brandLane && (kind === "competitor" || kind === "branded") ? (
          <Button
            variant="link"
            size="xs"
            className="h-auto w-fit justify-start p-0 text-[11px]"
            data-strategy-kind={kind}
            data-strategy-action="category-brand"
            disabled={mutationPending}
            onClick={() => openBrandEditor(kind)}
          >
            {t("strategy.matrix.configureBrand")}
          </Button>
        ) : null}
      </div>
    );
  }

  function renderBranch(kind: SmartCreationTargetingStrategyKind) {
    const row = MATRIX_ROWS[kind];
    const diagnostics = plan.strategyDiagnostics.filter(
      (diagnostic) => targetEditing && diagnostic.strategyKind === kind && diagnostic.blocking,
    );
    const hasEmptyLane =
      targetEditing &&
      [...row.keywords, ...row.products].some((choice) => emptyLanes.includes(choice.lane));
    return (
      <Field
        key={kind}
        data-strategy-branch={kind}
        invalid={diagnostics.length > 0 || hasEmptyLane}
        className={cn(
          "gap-0 border-b border-line bg-surface last:border-b-0",
          (diagnostics.length || hasEmptyLane) && "border-l-2 border-l-destructive",
        )}
      >
        <div className="grid w-full min-w-0 grid-cols-[minmax(112px,148px)_minmax(0,1fr)_minmax(0,1fr)]">
          <div className="flex flex-col justify-center gap-2 border-r border-line px-3 py-2">
            <div className="flex items-center gap-1">
              <span className="text-[13px] font-medium">
                {kind === "automatic" ? t("strategy.matrix.auto") : t(`strategy.${kind}`)}
              </span>
              <Popover>
                <PopoverTrigger
                  render={
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`${t(`strategy.${kind}`)} · ${t("strategy.evidenceDetails")}`}
                    />
                  }
                >
                  <IconInfoCircle className="size-3.5" />
                </PopoverTrigger>
                <PopoverPopup className="w-72">
                  <PopoverTitle>{t(`strategy.${kind}`)}</PopoverTitle>
                  <p className="mt-2 text-xs leading-5 text-muted-foreground">
                    {t(`strategy.${kind}Description`)}
                  </p>
                </PopoverPopup>
              </Popover>
            </div>
            {targetEditing ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto justify-start px-0 py-0 text-[11px] font-normal"
                data-strategy-kind={kind}
                data-strategy-action="targets"
                disabled={mutationPending}
                onClick={(event) => openTargets(event, kind)}
              >
                {t("strategy.reviewTargets")}
              </Button>
            ) : null}
          </div>
          {kind === "automatic" ? (
            <div className="col-span-2">
              {renderChoice(kind, row.products[0]!)}
              <p className="px-3 pb-3 text-xs leading-5">
                {branchFor(plan, "automatic")
                  .targets.filter((target) => target.included)
                  .map((target) =>
                    target.intent.type === "auto"
                      ? t(`review.preview.graph.expressions.${target.intent.expressionType}`)
                      : "",
                  )
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-col justify-center divide-y divide-line border-r border-line">
                {row.keywords.map((choice) => renderChoice(kind, choice))}
              </div>
              <div className="flex flex-col justify-center divide-y divide-line">
                {row.products.map((choice) => renderChoice(kind, choice))}
              </div>
            </>
          )}
        </div>
        {diagnostics.length ? (
          <FieldError match className="px-3 pb-2">
            {diagnostics.map((diagnostic) => diagnostic.message).join(" ")}
          </FieldError>
        ) : null}
      </Field>
    );
  }

  function renderGroup(
    group: "automatic" | "unbranded" | "branded",
    kinds: SmartCreationTargetingStrategyKind[],
  ) {
    return (
      <section
        data-strategy-group={group}
        className="overflow-hidden rounded-lg border border-line bg-surface"
      >
        <div className="grid min-h-9 grid-cols-[minmax(112px,148px)_minmax(0,1fr)_minmax(0,1fr)] items-center border-b border-line bg-subtle px-3">
          <h3 className="text-[13px] font-semibold">
            {t(`strategy.matrix.${group === "automatic" ? "auto" : group}`)}
          </h3>
          {group !== "automatic" ? (
            <>
              <span className="text-center text-xs text-muted-foreground">
                {t("strategy.matrix.keywordTargets")}
              </span>
              <span className="text-center text-xs text-muted-foreground">
                {t("strategy.matrix.productTargets")}
              </span>
            </>
          ) : null}
        </div>
        <div className="flex flex-col">{kinds.map(renderBranch)}</div>
      </section>
    );
  }

  return (
    <>
      <div className="@container flex min-w-0 flex-col gap-4">
        <StrategyContextSummary plan={plan} currencyCode={capabilities.currencyCode} />
        <div className="min-w-0 overflow-x-auto">
          <div data-strategy-matrix className="flex min-w-[560px] flex-col gap-2">
            {renderGroup("automatic", ["automatic"])}
            {renderGroup("unbranded", ["competitor", "research", "performance"])}
            {renderGroup("branded", ["branded"])}
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">
              {t("strategy.overviewCount", {
                enabled: branches.filter(
                  (branch) => effectiveSmartCreationStrategyLanes(branch).length,
                ).length,
                manual: manualTargets,
                auto: branchFor(plan, "automatic").targets.filter((target) => target.included)
                  .length,
              })}
            </span>
            {recommendation && recommendation.evidence.status !== "sufficient" ? (
              <span data-strategy-evidence-fallback className="text-xs text-muted-foreground">
                {t("strategy.noRecommendation")}
              </span>
            ) : null}
            {showBudgetLimitedPreset ? (
              <span data-strategy-budget-limited className="text-xs text-muted-foreground">
                {t("strategy.budgetLimitsPreset")}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-1">
            <Popover>
              <PopoverTrigger render={<Button type="button" variant="ghost" size="sm" />}>
                <IconInfoCircle data-icon="inline-start" />
                {t("strategy.evidenceDetails")}
              </PopoverTrigger>
              <PopoverPopup align="end" className="w-[360px]">
                <PopoverTitle>{t("strategy.evidenceDetails")}</PopoverTitle>
                <div className="mt-3 flex flex-col gap-3">
                  {refreshReason ? (
                    <p className="text-xs text-muted-foreground">
                      {t(`strategy.recommendationRefresh.${refreshReason}`)}
                    </p>
                  ) : null}
                  {recommendation ? (
                    <StrategyEvidenceDetails evidence={recommendation.evidence} />
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {t(
                        recommendationPending
                          ? "strategy.evidencePending"
                          : "strategy.noRecommendation",
                      )}
                    </p>
                  )}
                  {plan.strategyDiagnostics
                    .filter((diagnostic) => diagnostic.strategyKind === null)
                    .map((diagnostic) => (
                      <p
                        key={`${diagnostic.code}:${diagnostic.message}`}
                        className={cn(
                          "text-xs",
                          diagnostic.blocking ? "text-destructive" : "text-muted-foreground",
                        )}
                      >
                        {diagnostic.message}
                      </p>
                    ))}
                </div>
              </PopoverPopup>
            </Popover>
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              disabled={recommendationFeedbackPending || mutationFeedbackPending}
              aria-label={t(
                plan.strategyRecommendation
                  ? "strategy.refreshEvidence"
                  : "strategy.analyzeEvidence",
              )}
              onClick={() => {
                if (!recommendationPending && !mutationPending) onRefreshRecommendation();
              }}
            >
              {recommendationFeedbackPending ? <Spinner /> : <IconRefresh />}
            </Button>
          </div>
        </div>
      </div>
      <StrategyDiscardDialog
        open={discardDialogOpen}
        onOpenChange={setDiscardDialogOpen}
        onDiscard={returnToOverview}
      />
    </>
  );
}

function StrategyDiscardDialog({
  open,
  onOpenChange,
  onDiscard,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation("smartCreation");
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("strategy.discard.title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("strategy.discard.description")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            {t("strategy.discard.keepEditing")}
          </AlertDialogClose>
          <AlertDialogClose
            onClick={onDiscard}
            render={<Button type="button" variant="destructive" />}
          >
            {t("strategy.discard.action")}
          </AlertDialogClose>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}

export { recommendationRefreshReason as getStrategyRecommendationRefreshReason };
